'use client';

import { useEffect, useRef } from 'react';

import { useKinde } from '@/hooks/useKinde';
import { isTauri } from '@/tauri/utils';

import { getDefaultStore } from 'jotai';

import { kindeDesktopBridgeAtom } from '@heidi/auth/state';
import type { KindeDesktopBridge as KindeDesktopBridgeShape } from '../types';

/**
 * Mounts the Kinde-desktop bridge atom that the cache reads at
 * module level. `useKinde` is a React hook and can't be called from
 * `getAccessToken`'s module scope; this component bridges the two
 * by mirroring `useKinde().getToken` into the atom whenever the SDK
 * surface changes.
 *
 * Mount once in the full-app provider tree (alongside the scheduler
 * and the telemetry subscriber). The Tauri-only effect short-circuits
 * on web so this component is safe to mount unconditionally — it just
 * does nothing on browser builds.
 *
 * Do NOT mount in `MinimalProviders` — overlay windows don't drive
 * the main session's token reads.
 */
export function KindeDesktopBridgeMount(): null {
  const { getToken, isLoading, isAuthenticated } = useKinde();
  const isDesktop = isTauri();

  // The Tauri branch of useKinde wraps the SDK's getToken in a fresh
  // arrow function each render (per-render closure for option pass-
  // through), so `getToken` identity is unstable. Stash the latest
  // reference in a ref; the effect below registers a stable bridge
  // wrapper that always dispatches through `getTokenRef.current`. This
  // keeps the effect mount-only — without the ref, listing `getToken`
  // in deps would re-set the atom on every render of the bridge
  // component.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  // `isLoading` is a scalar (not a stable function) and flips during
  // the Kinde SDK's init window. Mirror it into a ref the same way so
  // the bridge exposes an `isLoading()` accessor that always reads
  // the latest value — without moving the atom write out of the
  // mount-only effect. `useKinde().isLoading` is `boolean | null`;
  // treat `null` as "still initialising" to be conservative (the
  // cache classifies loading as transient, which preserves state).
  const isLoadingRef = useRef<boolean>(isLoading !== false);
  isLoadingRef.current = isLoading !== false;

  // Mirror `useKinde().isAuthenticated` for the same reason. Consumed
  // only when `getToken()` resolved null AND `isLoading()` is false —
  // the Kinde React SDK's `useRefreshToken` catches token-endpoint
  // failures and resolves `undefined`, so a null result is ambiguous
  // between "refresh network blip" and "genuinely signed out". The
  // SDK's in-memory user object survives a swallowed refresh
  // failure, so this flag is the honest discriminator: `true` →
  // transient, `false` → bare-null. `boolean | null`; treat non-`true`
  // as not-authenticated for gate purposes (matches SDK semantics —
  // `null` means "unknown", which we should NOT trust as evidence
  // of a live session).
  const isAuthenticatedRef = useRef<boolean>(isAuthenticated === true);
  isAuthenticatedRef.current = isAuthenticated === true;

  useEffect(() => {
    if (!isDesktop) return;

    const bridge: KindeDesktopBridgeShape = {
      getToken: (options) => getTokenRef.current(options),
      isLoading: () => isLoadingRef.current,
      isAuthenticated: () => isAuthenticatedRef.current,
    };
    const store = getDefaultStore();
    store.set(kindeDesktopBridgeAtom, bridge);

    return () => {
      // Clear on unmount so a stale bridge reference can't outlive
      // its React owner (e.g. on a hot-module-reload during dev).
      store.set(kindeDesktopBridgeAtom, null);
    };
  }, [isDesktop]);

  return null;
}
